import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Boxes, ListOrdered, UserCheck, ScanLine, Undo2, ArrowLeft, Wrench, CheckCircle2, Activity, RotateCcw, HeartPulse, Loader2, Printer } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { filterOperations } from '../../../utils/operationAccess';

const API = process.env.REACT_APP_BACKEND_URL;

const TILES = [
  {
    id: 'combo-v2-planner',
    title: 'Bulk Planner (Matrix)',
    description: 'Plan combo assemblies by Design × Shade × Size matrix',
    icon: Boxes,
    route: '/wms/combo-v2/planner',
    color: 'bg-emerald-500',
  },
  {
    id: 'combo-v2-plans',
    title: 'All Plans',
    description: 'View, monitor, or cancel bulk assembly plans',
    icon: ListOrdered,
    route: '/wms/combo-v2/plans',
    color: 'bg-blue-500',
  },
  {
    id: 'combo-v2-distributor',
    title: 'Piece Distributor',
    description: 'Hand over picked bundles to Combo PP Staff (round-robin)',
    icon: UserCheck,
    route: '/wms/combo-v2/distributor',
    color: 'bg-purple-500',
  },
  {
    id: 'combo-v2-pp-station',
    title: 'PP Scan Station',
    description: 'Scan component USNs and save combos (stickers print at the Print Desk)',
    icon: ScanLine,
    route: '/wms/combo-v2/pp-station',
    color: 'bg-amber-500',
  },
  {
    id: 'combo-v2-print-desk',
    title: 'Sticker Print Desk',
    description: 'Enter a PP staff ID and print the combo stickers they saved',
    icon: Printer,
    route: '/wms/combo-v2/print-desk',
    color: 'bg-emerald-600',
  },
  {
    id: 'combo-v2-return-excess',
    title: 'Return Leftover USNs',
    description: 'Return floor leftovers to SFG storage as a new bundle',
    icon: Undo2,
    route: '/wms/combo-v2/distributor',
    color: 'bg-rose-500',
    note: 'Open from a plan inside Distributor',
  },
  {
    id: 'combo-v2-reversal',
    title: 'Combo Reversal (V2)',
    description: 'Disassemble an assembled V2 combo, reprint component labels, queue pieces for re-bundling',
    icon: RotateCcw,
    route: '/wms/combo-v2/reversal',
    color: 'bg-fuchsia-600',
  },
  {
    id: 'combo-floor-reconciliation',
    title: 'Floor Quick-Wrap (V1 + V2)',
    description: 'Scan stuck floor USNs, wrap into fresh SFG bundles for put-away',
    icon: Wrench,
    route: '/wms/admin/combo-floor-reconciliation',
    color: 'bg-orange-600',
  },
  {
    id: 'combo-successful-sessions',
    title: 'Successful Sessions Report',
    description: 'Completed combo sessions with USN-level component traceability',
    icon: CheckCircle2,
    route: '/wms/admin/combo-successful-sessions',
    color: 'bg-teal-600',
  },
  {
    id: 'combo-pp-staff-performance',
    title: 'PP Staff Performance',
    description: 'Combos assembled & components used per PP staff (V1 + V2)',
    icon: Activity,
    route: '/wms/combo/pp-staff-performance',
    color: 'bg-indigo-600',
  },
];

export default function ComboV2Dashboard() {
  const navigate = useNavigate();
  const visibleTiles = filterOperations('fg_wms', TILES);

  const [healOpen, setHealOpen] = useState(false);
  const [healLoading, setHealLoading] = useState(false);
  const [healResult, setHealResult] = useState(null);

  const runHeal = async (dryRun) => {
    try {
      setHealLoading(true);
      const tk = localStorage.getItem('session_token');
      const r = await axios.post(
        `${API}/api/combo-v2/distributor/admin/heal-stuck-consumed-bundles`,
        { dry_run: dryRun },
        { headers: { Authorization: `Bearer ${tk}` } },
      );
      setHealResult(r.data);
      if (!dryRun) {
        toast.success(`Healed ${r.data.bundles_marked_consumed} fully-consumed bundle(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Heal request failed');
    } finally {
      setHealLoading(false);
    }
  };

  const openHeal = () => {
    setHealResult(null);
    setHealOpen(true);
    runHeal(true);
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] p-6" data-testid="combo-v2-dashboard">
      <div className="max-w-[1100px] mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/wms/fg-dashboard')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-stone-900">Combo V2 — Bulk Assembly</h1>
            <p className="text-sm text-stone-500 mt-1">
              Streamlined matrix-driven combo assembly with distributor + PP scan flow.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            onClick={openHeal}
            data-testid="heal-stuck-v2-btn"
          >
            <HeartPulse className="h-4 w-4 mr-1" /> Heal Stuck Bundles
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleTiles.map(t => {
            const Icon = t.icon;
            return (
              <Card
                key={t.id}
                className={`p-5 cursor-pointer hover:shadow-md transition-shadow border-l-4 ${t.disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                style={{ borderLeftColor: t.disabled ? '#999' : undefined }}
                onClick={() => !t.disabled && navigate(t.route)}
                data-testid={`tile-${t.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`${t.color} text-white p-2 rounded`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-stone-900">{t.title}</h3>
                      {t.badge && (
                        <span className="text-[10px] bg-stone-200 text-stone-700 px-1.5 py-0.5 rounded">
                          {t.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-stone-500 mt-1">{t.description}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <Dialog open={healOpen} onOpenChange={setHealOpen}>
        <DialogContent data-testid="heal-stuck-v2-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HeartPulse className="h-5 w-5 text-rose-600" /> Heal Stuck Combo V2 Bundles
            </DialogTitle>
            <DialogDescription>
              Scans bundles stuck in <b>PICKED_FOR_COMBO_V2</b> and transitions only the
              <b> fully-consumed</b> ones (every piece assembled, returned, or damaged) to
              <b> CONSUMED</b>. Bundles with pieces still on the floor are left untouched.
            </DialogDescription>
          </DialogHeader>

          {healLoading && (
            <div className="flex items-center justify-center py-8 text-stone-500" data-testid="heal-loading">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Scanning…
            </div>
          )}

          {!healLoading && healResult && (
            <div className="space-y-3" data-testid="heal-result">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-stone-100 rounded p-2">
                  <div className="text-2xl font-bold text-stone-900" data-testid="heal-scanned">{healResult.stuck_bundles_scanned}</div>
                  <div className="text-[11px] text-stone-500">Stuck scanned</div>
                </div>
                <div className="bg-emerald-50 rounded p-2">
                  <div className="text-2xl font-bold text-emerald-700" data-testid="heal-fully">{healResult.fully_consumed}</div>
                  <div className="text-[11px] text-stone-500">Fully consumed</div>
                </div>
                <div className="bg-amber-50 rounded p-2">
                  <div className="text-2xl font-bold text-amber-700" data-testid="heal-left">{healResult.partially_consumed_left}</div>
                  <div className="text-[11px] text-stone-500">Still on floor</div>
                </div>
              </div>

              {Object.keys(healResult.by_plan_status || {}).length > 0 && (
                <div className="border rounded divide-y text-sm">
                  {Object.entries(healResult.by_plan_status).map(([st, v]) => (
                    <div key={st} className="flex items-center justify-between px-3 py-1.5">
                      <span className="font-medium text-stone-700">{st}</span>
                      <span className="text-stone-500">
                        {v.scanned} scanned · <span className="text-emerald-700">{v.fully_consumed} consumable</span> · {v.left} left
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {healResult.dry_run && healResult.fully_consumed > 0 && (
                <p className="text-xs text-stone-500">
                  Preview only — click <b>Apply Heal</b> to mark the {healResult.fully_consumed} fully-consumed bundle(s) as CONSUMED.
                </p>
              )}
              {healResult.dry_run && healResult.fully_consumed === 0 && (
                <p className="text-xs text-stone-500">No fully-consumed stuck bundles found. Nothing to heal.</p>
              )}
              {!healResult.dry_run && (
                <p className="text-xs text-emerald-700 font-medium" data-testid="heal-applied-msg">
                  ✓ Marked {healResult.bundles_marked_consumed} bundle(s) as CONSUMED.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setHealOpen(false)} data-testid="heal-close-btn">Close</Button>
            <Button
              onClick={() => runHeal(false)}
              disabled={healLoading || !healResult || !healResult.dry_run || healResult.fully_consumed === 0}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="heal-apply-btn"
            >
              {healLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Apply Heal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
